import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Flame, Check, ArrowLeft, Loader2, Zap, CheckCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import usePro from "@/hooks/usePro";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PricingPage() {
  const navigate = useNavigate();
  const { isPro, proEmail, unlockPro } = usePro();
  const [isLoading, setIsLoading] = useState(null); // "monthly" | "annual" | null
  const [unlockEmail, setUnlockEmail] = useState("");
  const [isUnlocking, setIsUnlocking] = useState(false);

  const handleCheckout = async (plan) => {
    setIsLoading(plan);
    try {
      const response = await axios.post(`${API}/stripe/create-checkout-session`, {
        plan: plan
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Unable to start checkout. Please try again.");
      setIsLoading(null);
    }
  };

  const handleUnlockPro = async () => {
    if (!unlockEmail.trim()) {
      toast.error("Please enter your email");
      return;
    }

    setIsUnlocking(true);
    try {
      const result = await unlockPro(unlockEmail.trim());
      if (result.success) {
        toast.success("Pro unlocked successfully!");
      } else {
        toast.error(result.message || "No active subscription found for this email");
      }
    } catch (error) {
      toast.error("Failed to verify subscription");
    } finally {
      setIsUnlocking(false);
    }
  };

  // If already Pro, show success state
  if (isPro) {
    return (
      <div className="min-h-screen bg-paper py-8 px-4">
        <div className="max-w-md mx-auto">
          <Card className="rounded-2xl shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="font-heading text-2xl text-sage-900 mb-2">You're Pro!</h2>
              <p className="font-body text-muted-foreground mb-2">
                Logged in as <span className="font-medium text-sage-900">{proEmail}</span>
              </p>
              <p className="font-body text-sm text-muted-foreground mb-6">
                You have access to all Pro features including unlimited challenge creation.
              </p>
              <Button
                onClick={() => navigate("/")}
                className="w-full rounded-full py-6 font-medium bg-ember hover:bg-ember/90 text-white"
              >
                Go to StreakChallenge
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-ember/10 rounded-2xl">
              <Flame className="w-10 h-10 text-ember" />
            </div>
            <h1 className="font-heading text-4xl font-light text-sage-900">
              StreakChallenge Pro
            </h1>
          </div>
          <p className="font-body text-lg text-muted-foreground">
            Upgrade to Pro for unlimited features
          </p>
        </div>

        {/* What Pro unlocks */}
        <Card className="border-border bg-card rounded-2xl shadow-sm mb-8">
          <CardContent className="p-6">
            <h2 className="font-heading text-lg text-sage-900 mb-4">What Pro unlocks</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-2 font-body text-sage-700">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                Unlimited challenge creation
              </div>
              <div className="flex items-center gap-2 font-body text-sage-700">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                Upload study guides (PDF/images)
              </div>
            </div>
            <p className="font-body text-sm text-muted-foreground mt-4 pt-4 border-t border-border">
              Only creators need Pro. Anyone with a shared link can practice for free.
            </p>
          </CardContent>
        </Card>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Monthly Plan */}
          <Card className="border-border bg-card rounded-2xl shadow-lg">
            <CardHeader className="text-center pb-2">
              <CardTitle className="font-heading text-xl text-sage-900">
                Pro Monthly
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <span className="font-heading text-5xl text-sage-900">$5</span>
                <span className="font-body text-muted-foreground">/month</span>
              </div>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 font-body text-sage-700">
                  <Check className="w-5 h-5 text-green-500" />
                  Unlimited challenges
                </li>
                <li className="flex items-center gap-2 font-body text-sage-700">
                  <Check className="w-5 h-5 text-green-500" />
                  Upload study materials
                </li>
                <li className="flex items-center gap-2 font-body text-sage-700">
                  <Check className="w-5 h-5 text-green-500" />
                  Priority support
                </li>
              </ul>

              <Button
                onClick={() => handleCheckout("monthly")}
                disabled={isLoading !== null}
                className="w-full rounded-full py-6 font-medium bg-sage-900 hover:bg-sage-900/90 text-white"
              >
                {isLoading === "monthly" ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</>
                ) : (
                  "Subscribe Monthly"
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Annual Plan */}
          <Card className="border-2 border-ember bg-card rounded-2xl shadow-lg relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-ember text-white px-4 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                <Zap className="w-4 h-4" />
                Save $10
              </span>
            </div>
            <CardHeader className="text-center pb-2 pt-8">
              <CardTitle className="font-heading text-xl text-sage-900">
                Pro Annual
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <span className="font-heading text-5xl text-ember">$50</span>
                <span className="font-body text-muted-foreground">/year</span>
              </div>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 font-body text-sage-700">
                  <Check className="w-5 h-5 text-green-500" />
                  Everything in Monthly
                </li>
                <li className="flex items-center gap-2 font-body text-sage-700">
                  <Check className="w-5 h-5 text-green-500" />
                  2 months free
                </li>
              </ul>

              <Button
                onClick={() => handleCheckout("annual")}
                disabled={isLoading !== null}
                className="w-full rounded-full py-6 font-medium bg-ember hover:bg-ember/90 text-white"
              >
                {isLoading === "annual" ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</>
                ) : (
                  "Subscribe Annually"
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Already Subscribed Section */}
        <Card className="border-border bg-card rounded-2xl shadow-lg mb-8">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-sage-100 rounded-lg">
                <Mail className="w-5 h-5 text-sage-700" />
              </div>
              <h3 className="font-heading text-lg text-sage-900">Already subscribed?</h3>
            </div>
            <p className="font-body text-sm text-muted-foreground mb-4">
              Enter your subscription email to unlock Pro features on this device.
            </p>
            <div className="flex gap-3">
              <Input
                type="email"
                placeholder="your@email.com"
                value={unlockEmail}
                onChange={(e) => setUnlockEmail(e.target.value)}
                className="flex-1 rounded-lg"
              />
              <Button
                onClick={handleUnlockPro}
                disabled={isUnlocking || !unlockEmail.trim()}
                className="rounded-full px-6 bg-sage-900 hover:bg-sage-900/90"
              >
                {isUnlocking ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Unlock Pro"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Back Button */}
        <div className="text-center">
          <Button
            onClick={() => navigate("/")}
            variant="outline"
            className="rounded-full px-8 py-6 font-medium"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to StreakChallenge
          </Button>
        </div>

        {/* Footer note */}
        <p className="text-center font-body text-sm text-muted-foreground mt-8">
          Secure checkout powered by Stripe. Cancel anytime.
        </p>
      </div>
    </div>
  );
}
